import { useState } from 'react';
import { Button, useMediaQuery } from '@mui/material';
import { theme } from 'assets/themes/zizzi';
import { ROUTES } from 'constants/routes';
import { pushAnalyticsEvent } from 'helpers/analytics';
import { useFetchFromService, usePageTitle } from 'hooks';
import Layout from 'layouts';
import { Voucher, SurprisePerkType } from 'models';
import { useLocalStorage, useStore } from 'providers';
import { useHistory } from 'react-router-dom';
import { getSurpriseVoucher } from 'services/loyaltyGateway';
import PerkzWalletModal from 'views/modals/PerkzWallet';
import { content } from './SurprisePerk.copy';
import SpinToWinAnimation from './SpinToWinAnimation/SpinToWinAnimation';
import SurprisePerkAnimation from './SurprisePerkAnimation/SurprisePerkAnimation';
import {
  StyledContainer,
  StyledPaper,
  Header,
  Title,
  SubTitle,
  Content,
  Text,
  Footer,
} from './SurprisePerk.styles';

interface ModalProps {
  perkzWallet: boolean;
  qrCode: boolean;
}

function SurprisePerk() {
  usePageTitle('Surprise Perk');

  const { store } = useStore();
  const { userData, vouchersData } = store;
  const { isEligibleForSurpriseVoucher } = userData?.loyaltyDetails || {};
  const { setToStorage } = useLocalStorage();

  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const history = useHistory();

  const type: SurprisePerkType = history.location.state?.type || 'surprise';
  const isSpinToWin = type === 'spin-to-win';
  const spinToWinVoucher = vouchersData?.active[0];

  const [surprisePerk, setSurprisePerk] = useState<Voucher>();
  const [modalOpen, setModalOpen] = useState<ModalProps>({ perkzWallet: false, qrCode: false });
  const [shouldAnimate, setShouldAnimate] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const fetchVouchersData = useFetchFromService('vouchersData');
  const fetchUserData = useFetchFromService('userData');

  const handleOnShowSurprisePerk = async () => {
    setModalOpen({ perkzWallet: true, qrCode: false });
    setToStorage('hasNewVouchers', true);
    fetchVouchersData();
    fetchUserData();
  };

  const handleOnClose = () => {
    setModalOpen({ perkzWallet: false, qrCode: false });
    history.push({ pathname: '/' });
  };

  const handleOnGetMorePerks = () => {
    history.push({ pathname: ROUTES.PERKZ_STORE });
  };

  const handleOnGetSurprisePerk = async () => {
    if (!isEligibleForSurpriseVoucher) return;

    setShouldAnimate(true);
    const {
      purchasedVouchers: [newSurprisePerk],
    } = await getSurpriseVoucher();
    setSurprisePerk(newSurprisePerk);
    setIsLoading(false);
    pushAnalyticsEvent({
      event: 'receivedSurprisePerk',
      reward: newSurprisePerk.title,
      voucher: newSurprisePerk.title,
    });
  };

  const handleOnGetWelcomePerk = async () => {
    setShouldAnimate(true);
    if (spinToWinVoucher) {
      setSurprisePerk(spinToWinVoucher);
      setIsLoading(false);
      pushAnalyticsEvent({
        event: 'receivedSpinToWinPerk',
        reward: spinToWinVoucher.title,
        voucher: spinToWinVoucher.title,
      });
    }
  };

  const perkzWalletModalProps = {
    isOpen: modalOpen.perkzWallet,
    onClose: handleOnClose,
    voucher: surprisePerk,
    showFooter: true,
    isLoading,
    onNext: handleOnGetMorePerks,
    displayModal: () => setModalOpen({ perkzWallet: isMobile, qrCode: true }),
  };

  const onGetPerk = isSpinToWin ? handleOnGetWelcomePerk : handleOnGetSurprisePerk;
  const { title, subtitle, text, subtext, callToAction } = content[type];

  return (
    <Layout>
      <StyledContainer>
        <StyledPaper>
          <Header>
            <Title variant="h1" alternative={`${isSpinToWin}`}>
              {title}
            </Title>
            <SubTitle variant="h2">{subtitle}</SubTitle>
          </Header>
          <Content>
            {isSpinToWin ? (
              <SpinToWinAnimation
                shouldAnimate={shouldAnimate}
                onAnimationEnd={handleOnShowSurprisePerk}
              />
            ) : (
              <SurprisePerkAnimation
                subtitle={subtitle}
                shouldAnimate={shouldAnimate}
                onAnimationEnd={handleOnShowSurprisePerk}
              />
            )}
            <Text variant="h4">{text}</Text>
            {subtext && <Text variant="h4">{subtext}</Text>}
          </Content>

          {callToAction && (
            <Footer>
              <Button
                disabled={!isSpinToWin && !isEligibleForSurpriseVoucher}
                onClick={onGetPerk}
                variant="contained"
                fullWidth
              >
                {callToAction}
              </Button>
            </Footer>
          )}
        </StyledPaper>
      </StyledContainer>
      <PerkzWalletModal {...perkzWalletModalProps} />
    </Layout>
  );
}

export default SurprisePerk;
